import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { api, problemOf } from '../api/client'
import { useCompanies } from '../api/hooks'
import type { Paged } from '../api/types'
import { Badge, Empty, fmtDate, fmtDateTime } from '../components/ui'

interface ImportIssue { level: 'error' | 'warning' | 'info'; sheet?: string; cell?: string; message: string }
interface QuestionMatch { questionNo: number; excelText?: string; optionLabel?: string; optionCode?: string; earnedPoints?: number; comment?: string; status: 'matched' | 'unmatched' | 'invalid_option' | 'missing'; questionId?: string; row: number }
interface FindingMatch { number?: number; groupCode?: string; severityLabel?: string; description?: string; actionText?: string; responsibleName?: string; dueDate?: string; actionNo?: string; questionNo?: number; status: string; row: number }
interface ImportPreview {
  jobId: string; status: string; fileName: string; sheetNames: string[]; detectedTemplateCode?: string; templateId?: string; templateVersionId?: string
  availableTemplates: { id: string; code: string; name: string }[]; companyId?: string; companyName?: string; header: Record<string, unknown>; code?: string
  auditors: string[]; dates: string[]; questions: QuestionMatch[]; findings: FindingMatch[]
  excelScore?: { percent?: number; grade?: string; groups: { number: number; name?: string; percent?: number; grade?: string }[] }
  issues: ImportIssue[]; matchedCount: number; unmatchedCount: number; missingCount: number; resultFormInstanceId?: string; scoreDiff?: string
}
interface ImportJobItem { id: string; fileName: string; status: string; detectedTemplateCode?: string; rowCount: number; errorCount: number; warningCount: number; resultFormInstanceId?: string; createdByName?: string; createdAt: string; completedAt?: string }

/** Excel import wizard: 1) upload → 2) detection/preview/matching → 3) commit. */
export default function ImportPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const jobId = params.get('job')
  const companies = useCompanies()
  const [drag, setDrag] = useState(false)
  const [companyId, setCompanyId] = useState('')
  const [tab, setTab] = useState<'questions' | 'findings' | 'issues'>('questions')

  const history = useQuery({ queryKey: ['imports'], queryFn: async () => (await api.get<Paged<ImportJobItem>>('/v1/imports?pageSize=20')).data })
  const preview = useQuery({ queryKey: ['import', jobId], queryFn: async () => (await api.get<ImportPreview>(`/v1/imports/${jobId}`)).data, enabled: !!jobId })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      if (companyId) form.append('companyId', companyId)
      return (await api.post<ImportPreview>('/v1/imports', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data
    },
    onSuccess: (p) => { qc.setQueryData(['import', p.jobId], p); setParams({ job: p.jobId }); void qc.invalidateQueries({ queryKey: ['imports'] }) },
  })
  const reparse = useMutation({
    mutationFn: async (templateId: string) => (await api.post<ImportPreview>(`/v1/imports/${jobId}/reparse`, { templateId, companyId: companyId || undefined })).data,
    onSuccess: (p) => qc.setQueryData(['import', p.jobId], p),
  })
  const commit = useMutation({
    mutationFn: async () => (await api.post<ImportPreview>(`/v1/imports/${jobId}/commit`, { companyId: companyId || preview.data?.companyId || undefined })).data,
    onSuccess: (p) => { qc.setQueryData(['import', p.jobId], p); void qc.invalidateQueries({ queryKey: ['imports'] }); void qc.invalidateQueries({ queryKey: ['reports'] }) },
  })

  function onDrop(e: DragEvent) {
    e.preventDefault()
    setDrag(false)
    const file = e.dataTransfer.files[0]
    if (file) upload.mutate(file)
  }

  const p = preview.data
  const errors = p?.issues.filter((i) => i.level === 'error') ?? []
  const canCommit = p && p.status === 'AwaitingConfirmation' && p.templateVersionId && (companyId || p.companyId) && errors.filter((e) => !e.message.includes('tanınamadı')).length === 0

  return (
    <div className="stack">
      <h2>{t('import.title')}</h2>

      {!jobId ? (
        <section className="card stack">
          <div className="grid two">
            <label>{t('companies.title')} ({t('import.optional')})
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value="">{t('import.autoDetectCompany')}</option>
                {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          </div>
          <div className={`dropzone big ${drag ? 'drag' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
            <p>{t('import.dropHint')}</p>
            <label className="button">{t('import.chooseFile')}<input type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} /></label>
            {upload.isPending ? <p className="muted">{t('import.uploading')}</p> : null}
            {upload.isError ? <p className="error">{problemOf(upload.error).title}</p> : null}
          </div>
          <p className="muted small">{t('import.formats')}</p>
        </section>
      ) : preview.isLoading || !p ? <p>{t('common.loading')}</p> : (
        <>
          <section className="card stack">
            <div className="row between wrap">
              <div>
                <b>{p.fileName}</b> <span className="muted small">· {p.sheetNames.join(', ')}</span>
                <div className="row wrap" style={{ marginTop: 6 }}>
                  <Badge color={p.status === 'Completed' ? '#008300' : '#2a78d6'}>{t(`importStatus.${p.status}`)}</Badge>
                  {p.detectedTemplateCode ? <Badge color="#008300">{t('import.detected')}: {p.detectedTemplateCode}</Badge> : <Badge color="#e34948">{t('import.notDetected')}</Badge>}
                  {p.code ? <Badge>{p.code}</Badge> : null}
                </div>
              </div>
              <button className="secondary" onClick={() => setParams({})}>{t('import.newImport')}</button>
            </div>

            {p.status !== 'Completed' ? (
              <div className="grid two">
                <label>{t('reports.template')}
                  <select value={p.templateId ?? ''} onChange={(e) => e.target.value && reparse.mutate(e.target.value)}>
                    <option value="">–</option>
                    {p.availableTemplates.map((x) => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}
                  </select>
                </label>
                <label>{t('companies.title')}
                  <select value={companyId || p.companyId || ''} onChange={(e) => setCompanyId(e.target.value)}>
                    <option value="">–</option>
                    {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {!p.companyId && p.companyName ? <span className="muted small">{t('import.excelCompany')}: {p.companyName}</span> : null}
                </label>
              </div>
            ) : null}

            <dl className="dl">
              <div><dt>{t('reports.date')}</dt><dd>{p.dates.map(fmtDate).join(', ') || '–'}</dd></div>
              <div><dt>{t('reports.auditors')}</dt><dd>{p.auditors.join(' / ') || '–'}</dd></div>
              {Object.entries(p.header).filter(([k]) => !k.toLowerCase().includes('date')).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{String(v ?? '')}</dd></div>)}
              {p.excelScore?.percent != null ? <div><dt>{t('import.excelScore')}</dt><dd>%{p.excelScore.percent} {p.excelScore.grade}</dd></div> : null}
            </dl>

            <div className="row wrap">
              <Badge color="#008300">{t('import.matched')}: {p.matchedCount}</Badge>
              <Badge color={p.unmatchedCount > 0 ? '#e34948' : undefined}>{t('import.unmatched')}: {p.unmatchedCount}</Badge>
              <Badge color={p.missingCount > 0 ? '#eda100' : undefined}>{t('import.missing')}: {p.missingCount}</Badge>
              <Badge color={errors.length > 0 ? '#e34948' : undefined}>{t('import.errors')}: {errors.length}</Badge>
              <Badge>{t('import.warnings')}: {p.issues.filter((i) => i.level === 'warning').length}</Badge>
            </div>

            {p.status === 'Completed' && p.resultFormInstanceId ? (
              <div className="card" style={{ background: 'var(--hover)' }}>
                <p>{t('import.done')} <Link to={`/reports/${p.resultFormInstanceId}`}>{t('reports.open')}</Link></p>
                {p.scoreDiff ? <p className="error small">{t('import.scoreDiff')}: <code>{p.scoreDiff}</code></p> : <p className="muted small">{t('import.scoreMatch')}</p>}
              </div>
            ) : (
              <div className="row">
                <button onClick={() => commit.mutate()} disabled={!canCommit || commit.isPending}>{t('import.commit')}</button>
                {commit.isError ? <span className="error">{problemOf(commit.error).title}</span> : null}
              </div>
            )}
          </section>

          <div className="tabs" role="tablist">
            <button className={tab === 'questions' ? 'active' : ''} onClick={() => setTab('questions')}>{t('reports.answers')} ({p.questions.length})</button>
            <button className={tab === 'findings' ? 'active' : ''} onClick={() => setTab('findings')}>{t('reports.findings')} ({p.findings.length})</button>
            <button className={tab === 'issues' ? 'active' : ''} onClick={() => setTab('issues')}>{t('import.issues')} ({p.issues.length})</button>
          </div>

          <section className="card table-wrap">
            {tab === 'questions' ? (
              <table className="table compact">
                <thead><tr><th>{t('import.row')}</th><th>S.NO</th><th>{t('reports.question')}</th><th>{t('reports.answer')}</th><th className="num">{t('score.earned')}</th><th>{t('reports.comment')}</th><th>{t('import.status')}</th></tr></thead>
                <tbody>
                  {p.questions.map((q) => (
                    <tr key={`${q.questionNo}-${q.row}`} className={q.status !== 'matched' ? 'warn-row' : ''}>
                      <td className="muted">{q.row || '–'}</td><td>{q.questionNo}</td><td className="small">{q.excelText}</td>
                      <td>{q.optionLabel ?? q.optionCode ?? '–'}</td><td className="num">{q.earnedPoints ?? ''}</td><td className="small">{q.comment}</td>
                      <td><Badge color={q.status === 'matched' ? '#008300' : q.status === 'missing' ? '#eda100' : '#e34948'}>{t(`import.matchStatus.${q.status}`)}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : tab === 'findings' ? (
              p.findings.length === 0 ? <Empty text={t('common.none')} /> : (
                <table className="table compact">
                  <thead><tr><th>NO</th><th>{t('finding.group')}</th><th>{t('finding.severity')}</th><th>{t('finding.description')}</th><th>{t('finding.action')}</th><th>{t('finding.responsible')}</th><th>{t('finding.dueDate')}</th><th>{t('finding.actionNo')}</th><th>{t('import.status')}</th></tr></thead>
                  <tbody>
                    {p.findings.map((f) => (
                      <tr key={f.row}>
                        <td>{f.number ?? '–'}</td><td className="small">{f.groupCode}</td><td>{f.severityLabel}</td><td className="small">{f.description}</td><td className="small">{f.actionText}</td>
                        <td className="small">{f.responsibleName}</td><td>{fmtDate(f.dueDate)}</td><td>{f.actionNo}</td>
                        <td><Badge color={f.status === 'matched' ? '#008300' : '#e34948'}>{t(`import.matchStatus.${f.status}`)}{f.questionNo ? ` (S.${f.questionNo})` : ''}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : (
              p.issues.length === 0 ? <Empty text={t('import.noIssues')} /> : (
                <table className="table compact">
                  <thead><tr><th>{t('import.level')}</th><th>{t('import.location')}</th><th>{t('import.message')}</th></tr></thead>
                  <tbody>
                    {p.issues.map((i, idx) => (
                      <tr key={idx}><td><Badge color={i.level === 'error' ? '#e34948' : '#eda100'}>{i.level}</Badge></td><td className="small">{i.sheet}{i.cell ? `!${i.cell}` : ''}</td><td>{i.message}</td></tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </section>
        </>
      )}

      <section className="card">
        <h3>{t('import.history')}</h3>
        {!history.data?.items.length ? <Empty text={t('common.none')} /> : (
          <div className="table-wrap">
            <table className="table compact">
              <thead><tr><th>{t('import.file')}</th><th>{t('reports.template')}</th><th>{t('import.status')}</th><th className="num">{t('import.rows')}</th><th className="num">{t('import.errors')}</th><th>{t('import.by')}</th><th>{t('reports.date')}</th><th /></tr></thead>
              <tbody>
                {history.data.items.map((j) => (
                  <tr key={j.id}>
                    <td><button className="link" onClick={() => setParams({ job: j.id })}>{j.fileName}</button></td>
                    <td>{j.detectedTemplateCode ?? '–'}</td>
                    <td><Badge color={j.status === 'Completed' ? '#008300' : j.status === 'Failed' ? '#e34948' : undefined}>{t(`importStatus.${j.status}`)}</Badge></td>
                    <td className="num">{j.rowCount}</td><td className="num">{j.errorCount}</td><td className="small">{j.createdByName}</td><td>{fmtDateTime(j.createdAt)}</td>
                    <td>{j.resultFormInstanceId ? <Link to={`/reports/${j.resultFormInstanceId}`}>{t('reports.open')}</Link> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
